import * as Sentry from "@sentry/react";
import { 
  createRoutesFromChildren, 
  matchRoutes, 
  useLocation, 
  useNavigationType 
} from "react-router-dom";
import React from "react";

export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN || "https://923c6dc4701f9a394dfd95a4ac55957f@o4509693652762624.ingest.us.sentry.io/4509693657088000";
  
  console.log('🔧 Sentry 초기화 시작...');
  console.log('📍 DSN:', dsn);
  console.log('🌍 Environment:', import.meta.env.MODE);
  console.log('🏗️ DEV Mode:', import.meta.env.DEV);
  
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE, // development, production
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    
    // PII(개인 식별 정보) 수집 활성화 - IP 주소, 사용자 정보 등 자동 수집
    sendDefaultPii: true,
    
    // 성능 모니터링 설정
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 프로덕션에서는 10%만 추적
    
    // 에러 필터링 (불필요한 에러 제외)
    beforeSend(event) {
      console.log('📤 Sentry beforeSend 호출됨:', event);
      
      // 개발 환경에서는 콘솔에도 출력
      if (import.meta.env.DEV) {
        console.error('🚨 Sentry Error 전송 중:', event);
      }
      
      // 특정 에러는 무시 (예: 네트워크 에러 등)
      if (event.exception?.values?.[0]?.value?.includes('Network Error')) {
        console.log('❌ Network Error 무시됨');
        return null;
      }
      
      // 개발 환경에서도 전송하도록 설정 (테스트용)
      console.log('✅ Sentry로 에러 전송 진행');
      return event;
    },
    
    // 사용자 컨텍스트 설정
    initialScope: {
      tags: {
        component: "refurb-buy-frontend"
      },
    },
  });
  
  console.log('✅ Sentry 초기화 완료!');
  
  // 테스트용 즉시 전송
  console.log('🧪 Sentry 연결 테스트 중...');
  Sentry.captureMessage('Sentry 초기화 완료 - 연결 테스트', 'info');
};

// 사용자 정보 설정 함수
export const setSentryUser = (user: { id?: string; name?: string; phone?: string }) => {
  Sentry.setUser({
    id: user.id || user.phone,
    username: user.name,
    phone: user.phone,
  });
};

// 커스텀 에러 로깅
export const logError = (error: Error, context?: Record<string, any>) => {
  Sentry.withScope((scope) => {
    if (context) {
      scope.setContext("additional_info", context);
    }
    scope.setLevel('error');
    Sentry.captureException(error);
  });
};

// 비즈니스 로직 에러 추적
export const trackBusinessError = (
  errorType: 'application_submit' | 'payment_process' | 'auth_failure' | 'api_call' | 'biztalk_send',
  message: string,
  data?: Record<string, any>
) => {
  Sentry.addBreadcrumb({
    message,
    category: errorType,
    data,
    level: 'error',
  });
  
  Sentry.captureMessage(`Business Error: ${errorType} - ${message}`, 'error');
};

// 성공적인 비즈니스 이벤트 추적
export const trackBusinessSuccess = (
  eventType: 'application_submit' | 'auth_complete' | 'payment_complete' | 'biztalk_sent',
  message: string,
  data?: Record<string, any>
) => {
  Sentry.addBreadcrumb({
    message,
    category: eventType,
    data,
    level: 'info',
  });
}; 